import { useMemo } from "react";
import { BuildingModel } from "./BuildingModel";
import { ExplodedBuildingModel } from "./ExplodedBuildingModel";
import { BuildingArrow } from "./BuildingArrow";
import { MergedBuildingEdges } from "./MergedBuildingEdges";
import { getBuildingStructure } from "../../config/buildingPresets";
import type { PlacedObject } from "../../config/types";

interface InteractiveBuildingsProps {
  buildings: PlacedObject[];
  hoveredBuildingId?: string | null;
  myCursorColor?: string; // 내 커서 색상
  ghostMode?: boolean; // 고스트 모드 (에디터 모드일 때 반투명)
  selectedBuildingId?: string | null; // 선택된 건물 ID (선택 시 다른 건물 투명 처리)
}

/**
 * 인터랙티브 건물 목록 컴포넌트
 * 모든 배치된 건물을 렌더링
 * 클릭 이벤트는 바닥 mesh에서 통합 처리 (hoveredPosition 기반)
 *
 * 최적화: 엣지는 MergedBuildingEdges로 병합하여 draw calls 감소
 *
 * 선택된 건물: ExplodedBuildingModel (벽면 폭발 + 텍스트 등장 애니메이션)
 * 비선택 건물: BuildingModel (외벽 텍스트 숨김)
 */
export function InteractiveBuildings({
  buildings,
  hoveredBuildingId = null,
  myCursorColor = "#4ECDC4", // 기본 색상
  ghostMode = false,
  selectedBuildingId = null, // 선택된 건물 ID
}: InteractiveBuildingsProps) {
  // 호버된 건물 정보
  const hoveredBuilding = useMemo(() => {
    if (!hoveredBuildingId) return null;
    return buildings.find((b) => b.id === hoveredBuildingId) || null;
  }, [buildings, hoveredBuildingId]);

  // 선택된 건물 정보
  const selectedBuilding = useMemo(() => {
    if (!selectedBuildingId) return null;
    return buildings.find((b) => b.id === selectedBuildingId) || null;
  }, [buildings, selectedBuildingId]);

  // 건물 높이 계산 헬퍼
  const getBuildingHeight = (building: PlacedObject): number => {
    if (
      !building.buildingStructure ||
      building.buildingStructure.length === 0
    ) {
      return 2; // 기본 높이
    }
    return building.buildingStructure.reduce((max, box) => {
      const height = box.position[1] + box.scale[1] / 2;
      return Math.max(max, height);
    }, 0);
  };

  // 건물별 투명도 계산 (선택된 건물이 있으면 다른 건물은 반투명)
  const getBuildingOpacity = (buildingId: string): number => {
    if (ghostMode) return 0.3;
    if (selectedBuildingId) {
      // 선택된 건물은 불투명, 나머지는 반투명
      return buildingId === selectedBuildingId ? 1.0 : 0.15;
    }
    return 1.0;
  };

  // 선택되지 않은 건물들
  const nonSelectedBuildings = useMemo(() => {
    return buildings.filter((b) => b.id !== selectedBuildingId);
  }, [buildings, selectedBuildingId]);

  return (
    <>
      {/* 비선택 건물들 (외벽 텍스트 숨김) */}
      {nonSelectedBuildings.map((building) => (
        <BuildingModel
          key={building.id}
          id={building.id}
          position={building.position}
          meshIndex={building.meshIndex}
          buildingStructure={building.buildingStructure}
          title={""}
          renderEdges={false} // 엣지는 병합으로 렌더링
          isHovered={building.id === hoveredBuildingId}
          userColor={building.userColor}
          tenants={[]} // 외벽 텍스트 숨김 (빈 배열)
          opacity={getBuildingOpacity(building.id)}
          useCSGEngraving={false} // CSG 음각도 비활성화
        />
      ))}

      {/* 선택된 건물: 폭발 애니메이션 모델 */}
      {selectedBuilding && (
        <ExplodedBuildingModel
          id={selectedBuilding.id}
          position={selectedBuilding.position}
          structure={
            selectedBuilding.buildingStructure?.length
              ? selectedBuilding.buildingStructure
              : getBuildingStructure(selectedBuilding.meshIndex)
          }
          userColor={selectedBuilding.userColor || "#888888"}
          isSelected={true}
          opacity={1.0}
        />
      )}

      {/* 호버된 건물 위 화살표 (고스트 모드가 아닐 때만, 선택된 건물 아닐 때만) */}
      {!ghostMode &&
        hoveredBuilding &&
        hoveredBuilding.id !== selectedBuildingId && (
          <BuildingArrow
            position={hoveredBuilding.position}
            buildingHeight={getBuildingHeight(hoveredBuilding)}
            isVisible={true}
            color={myCursorColor}
          />
        )}

      {/* 병합된 엣지 (1 draw call) - 선택된 건물 제외 */}
      <MergedBuildingEdges
        buildings={nonSelectedBuildings}
        hoveredBuildingId={hoveredBuildingId}
        opacity={ghostMode ? 0.2 : selectedBuildingId ? 0.3 : 1.0}
        selectedBuildingId={null} // 선택된 건물은 ExplodedBuildingModel에서 처리
      />
    </>
  );
}
